import 'lenis/dist/lenis.css';
import Lenis from 'lenis';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { initAnimations } from './globalAnimations';

gsap.registerPlugin(ScrollTrigger);

// Smooth scroll — disabled on iOS Safari only (native touch scroll is better with video)
const isIosSafari = /iP(ad|hone|od)/.test(navigator.userAgent)
  && /WebKit/.test(navigator.userAgent)
  && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);

// Must be registered BEFORE new Lenis() so it runs first and can block Lenis's listener
window.addEventListener('wheel', (e) => {
  const dropdown = document.querySelector('.select2-dropdown');
  if (dropdown && (dropdown === e.target || dropdown.contains(e.target))) {
    e.stopImmediatePropagation();
  }
}, { passive: true });

// In-page # links: browser default + `scroll-behavior: auto` = instant jump; Lenis must animate.
// https://github.com/darkroomengineering/lenis#anchor-links
const lenisAnchorOptions = { offset: 0, duration: 1.1, force: true };

function normalizePathForCompare(path) {
  if (!path || path === '/') {
    return '/';
  }
  return path.replace(/\/+$/, '') || '/';
}

function isSamePagePath(aPathname, bPathname) {
  return normalizePathForCompare(aPathname) === normalizePathForCompare(bPathname);
}

/**
 * WP / `querySelector("#a.b")` bugfix: use getElementById + CSS.escape fallback.
 */
function elementFromHash(hash) {
  if (!hash || hash === '#' || hash.length < 2) {
    return null;
  }
  const raw = hash.slice(1);
  let id;
  try {
    id = decodeURIComponent(raw);
  } catch {
    id = raw;
  }
  const byId = document.getElementById(id);
  if (byId) {
    return byId;
  }
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    try {
      return document.querySelector(`[id="${CSS.escape(id)}"]`);
    } catch {
      return null;
    }
  }
  return null;
}

export const lenis = new Lenis({
  lerp: 0.075,
  wheelMultiplier: 1.3,
  touchMultiplier: isIosSafari ? 0 : 1.3,
  keyboard: false,
  prevent: () => isIosSafari,
  // Hash navigation handled in `initLenisInPageAnchorCapture` (preventDefault + Lenis scroll).
  anchors: false,
  stopInertiaOnNavigate: !isIosSafari,
});

lenis.on('scroll', ScrollTrigger.update);

gsap.ticker.add((time) => {
  lenis.raf(time * 1000);
});

function getLenisAnchorScrollOptions() {
  return lenisAnchorOptions;
}

/**
 * Same-page `a[href*="#"]`: `preventDefault` stops native (instant) jump; Lenis does smooth scroll.
 */
function initLenisInPageAnchorCapture() {
  document.addEventListener(
    'click',
    (e) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      const a = e.target.closest('a[href*="#"]');
      if (!a) {
        return;
      }
      const href = a.getAttribute('href');
      if (!href || href === '#') {
        return;
      }
      let linkUrl;
      try {
        linkUrl = new URL(a.href, window.location.href);
      } catch {
        return;
      }
      if (linkUrl.origin !== window.location.origin) {
        return;
      }
      if (!isSamePagePath(linkUrl.pathname, window.location.pathname)) {
        return;
      }
      if (!linkUrl.hash || linkUrl.hash === '#') {
        return;
      }
      const targetEl = elementFromHash(linkUrl.hash);
      if (!targetEl) {
        return;
      }

      e.preventDefault();
      history.pushState(
        null,
        '',
        `${linkUrl.pathname}${linkUrl.search}${linkUrl.hash}`,
      );

      requestAnimationFrame(() => {
        lenis.resize();
        lenis.scrollTo(targetEl, getLenisAnchorScrollOptions());
      });
    },
    true,
  );
}

/**
 * /page#section — reset scroll so Lenis is not "already at target" (else animation is skipped).
 */
function initLenisHashAndHistory() {
  const scrollToTarget = (hash) => {
    const el = elementFromHash(hash);
    if (!el) {
      return;
    }
    lenis.resize();
    lenis.scrollTo(el, getLenisAnchorScrollOptions());
  };

  const runWhenReady = (fn) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(fn);
    });
  };

  if (window.location.hash) {
    history.scrollRestoration = 'manual';
    const preloader = document.querySelector('.preloader');
    let preloadTime = 10;

    setTimeout(() => {
      window.scrollTo(0, 0);
      lenis.scrollTo(0, { immediate: true });
    }, 5);

    if (preloader) {
      preloadTime = 2200;
    }

    window.addEventListener('load', () => {
      setTimeout(() => {
        runWhenReady(() => scrollToTarget(window.location.hash));    
      }, preloadTime);
    });
  }

  window.addEventListener('hashchange', () => {
    scrollToTarget(window.location.hash);
  });
}

export const initAnim = () => {
  initLenisInPageAnchorCapture();
  initLenisHashAndHistory();

  // Prevent page scrolling on press space
  window.addEventListener('keydown', (event) => {
    const isEditable = ['INPUT', 'TEXTAREA'].includes(event.target.tagName) || event.target.isContentEditable;

    if (!isEditable  && event.code === 'Space') {
      event.preventDefault();
    }
  });

  initAnimations()

  function customImgParallax() {
    gsap.utils.toArray(".js-parallax").forEach((section) => {
      section.style.backgroundPosition = `0% ${-innerHeight / 7}px`;

      gsap.to(section, {
        backgroundPosition: `0% ${innerHeight / 14}px`,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          scrub: true,
        },
      });
    });

    gsap.utils.toArray(".js-parallax-big").forEach((section) => {
      section.style.backgroundPosition = `0% ${-innerHeight / 3}px`;

      gsap.to(section, {
        backgroundPosition: `0% ${innerHeight / 5}px`,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          scrub: true,
        },
      });
    });

    gsap.utils.toArray(".js-parallax-video").forEach((wrapper) => {
      const video = wrapper.querySelector("video");
      if (!video) return;

      gsap.set(video, { yPercent: -15 });

      gsap.to(video, {
        yPercent: 15,
        ease: "none",
        scrollTrigger: {
          trigger: wrapper,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    });

    gsap.utils.toArray(".js-parallax-img").forEach((section) => {
      section.style.top = `${-innerHeight / 15}px`;

      gsap.to(section, {
        top: `${innerHeight / 25}px`,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          scrub: true,
        },
      });
    });

    const hero = document.querySelector(".js-parallax-hero");

    function setHeroParallax() {
      if (!hero) return;

      const isMobile = window.innerWidth < 768;
      hero.style.backgroundPosition = isMobile ? "77% 0px" : "50% 0px";

      ScrollTrigger.getById("hero-parallax")?.kill();

      gsap.to(hero, {
        backgroundPositionY: () => `${innerHeight / 5}px`,
        ease: "none",
        scrollTrigger: {
          id: "hero-parallax",
          trigger: hero,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    }
    setHeroParallax();
    window.addEventListener("resize", setHeroParallax);

    const heroVideo = document.querySelector(".js-parallax-hero-video");

    function setHeroVideoParallax() {
      if (!heroVideo) return;
      const video = heroVideo.querySelector("video");
      if (!video) return;

      ScrollTrigger.getById("hero-video-parallax")?.kill();

      gsap.set(video, { yPercent: -15 });

      gsap.to(video, {
        yPercent: 15,
        ease: "none",
        scrollTrigger: {
          id: "hero-video-parallax",
          trigger: heroVideo,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    }
    setHeroVideoParallax();
    window.addEventListener("resize", setHeroVideoParallax);

    const footerParalax = document.querySelector(".js-parallax-footer");

    function setFooterParallax() {
      if (!footerParalax) return;
      footerParalax.style.backgroundPosition = `50% ${innerHeight / 5}px`;

      ScrollTrigger.getById("footer-parallax")?.kill();

      gsap.to(footerParalax, {
        backgroundPositionY: () => `0px`,
        ease: "none",
        scrollTrigger: {
          id: "footer-parallax",
          trigger: footerParalax,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    }

    setFooterParallax();
    window.addEventListener("resize", setFooterParallax);

    function observeNavButtonsWithScrollTrigger() {
      const navWrapper = document.querySelector('.nav-buttons-wrapper');
      const headerButtons = document.querySelector('.header__buttons');
      if (!navWrapper || !headerButtons || typeof ScrollTrigger === 'undefined') return;

      const st = ScrollTrigger.create({
        trigger: navWrapper,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: () => headerButtons.classList.add('hidden'),
        onEnterBack: () => headerButtons.classList.add('hidden'),
        onLeave: () => headerButtons.classList.remove('hidden'),
        onLeaveBack: () => headerButtons.classList.remove('hidden'),
      });

      st;
    }

    observeNavButtonsWithScrollTrigger();
  }  

  return {
    init() {
      customImgParallax();
    },
  }
};
